import { useEffect, useRef, useState, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { NotificationProvider } from "@/context/NotificationContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import FinAILoader from "@/components/FinAILoader";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VerifyEmailPending from "@/pages/VerifyEmailPending";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import ProtectedRoute from "@/components/ProtectedRoute";

// Free Tools
import TaxCalculator from "@/pages/tools/TaxCalculator";
import InvoiceGenerator from "@/pages/tools/InvoiceGenerator";
import ExpenseTracker from "@/pages/tools/ExpenseTracker";

// Dashboard Pages (New Enhanced Dashboard)
import DashboardHomePage from "@/pages/dashboard/DashboardHomePage";
import TasksPage from "@/pages/dashboard/TasksPage";
import InvoicingPage from "@/pages/dashboard/InvoicingPage";
import PaymentLinksPage from "@/pages/dashboard/PaymentLinksPage";
import MyPayoutsPage from "@/pages/dashboard/MyPayoutsPage";
import BillPayPage from "@/pages/dashboard/BillPayPage";
import ContractsPage from "@/pages/dashboard/ContractsPage";
import TaxHubPage from "@/pages/dashboard/TaxHubPage";
import SCorpPage from "@/pages/dashboard/SCorpPage";
import DocumentsPage from "@/pages/dashboard/DocumentsPage";

// Settings Pages
import PaymentMethodsPage from "@/pages/dashboard/settings/PaymentMethodsPage";
import RecipientsPage from "@/pages/dashboard/settings/RecipientsPage";
import CompanyProfilePage from "@/pages/dashboard/settings/CompanyProfilePage";
import TeamPage from "@/pages/dashboard/settings/TeamPage";
import BankConnectionsPage from "@/pages/dashboard/settings/BankConnectionsPage";
import DocuHubPage from "@/pages/dashboard/settings/DocuHubPage";
import PlansPage from "@/pages/dashboard/settings/PlansPage";
import StripeIntegrationPage from "@/pages/dashboard/settings/StripeIntegrationPage";
import PlaidOAuthCallback from "@/pages/PlaidOAuthCallback";
import SignDocumentPage from "@/pages/SignDocumentPage";

// Legal Pages
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import SecurityCompliance from "@/pages/legal/SecurityCompliance";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import DataUsePolicy from "@/pages/legal/DataUsePolicy";
import FAQ from "@/pages/legal/FAQ";
import { API_URL } from "@/utils/api";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/oauth-callback" element={<PlaidOAuthCallback />} />
      <Route path="/sign-document/:sigRequestId" element={<SignDocumentPage />} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/security" element={<SecurityCompliance />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/data-use" element={<DataUsePolicy />} />
      <Route path="/faq" element={<FAQ />} />
      
      {/* Free Tools Routes */}
      <Route path="/tools/tax-calculator" element={<TaxCalculator />} />
      <Route path="/tools/invoice" element={<InvoiceGenerator />} />
      <Route path="/tools/expenses" element={<ExpenseTracker />} />
      
      {/* Enhanced User Dashboard Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardHomePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/tasks" 
        element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/invoicing" 
        element={
          <ProtectedRoute>
            <InvoicingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/payment-links" 
        element={
          <ProtectedRoute>
            <PaymentLinksPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/my-payouts" 
        element={
          <ProtectedRoute>
            <MyPayoutsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/bill-pay" 
        element={
          <ProtectedRoute>
            <BillPayPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/contracts" 
        element={
          <ProtectedRoute>
            <ContractsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/tax-hub" 
        element={
          <ProtectedRoute>
            <TaxHubPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/scorp" 
        element={
          <ProtectedRoute>
            <SCorpPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/documents" 
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Settings Routes */}
      <Route 
        path="/dashboard/settings/payments" 
        element={
          <ProtectedRoute>
            <PaymentMethodsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/settings/recipients" 
        element={
          <ProtectedRoute>
            <RecipientsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/settings/company" 
        element={
          <ProtectedRoute>
            <CompanyProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/settings/team" 
        element={
          <ProtectedRoute>
            <TeamPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/settings/banks" 
        element={
          <ProtectedRoute>
            <BankConnectionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/settings/bank" 
        element={
          <ProtectedRoute>
            <BankConnectionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/settings/docuhub" 
        element={
          <ProtectedRoute>
            <DocuHubPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/plans" 
        element={
          <ProtectedRoute>
            <PlansPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/settings/stripe" 
        element={
          <ProtectedRoute>
            <StripeIntegrationPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requiredRoles={["admin", "finance_admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance" 
        element={
          <ProtectedRoute requiredRoles={["admin", "finance_admin", "finance"]}>
            <AdminDashboard viewOnly={true} />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  const [isAppLoading, setIsAppLoading] = useState(true);

  useEffect(() => {
    // Simulate initial app load / hydration
    const timer = setTimeout(() => {
      setIsAppLoading(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  if (isAppLoading) {
    return <FinAILoader message="Starting FinAI" />;
  }

  return (
    <NotificationProvider>
      <CurrencyProvider>
        <div className="App min-h-screen bg-white">
          <BrowserRouter>
            <Suspense fallback={<FinAILoader message="Loading page" />}>
              <AppRouter />
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </CurrencyProvider>
    </NotificationProvider>
  );
}

export default App;
